/**
 * La cabecera de la home DE SIEMPRE (todo grupo que no es C): "Elegí qué querés crear"
 * con las dos cards de VERTIENTE —"Crear imágenes" / "Crear videos"— que abren el popup
 * con los modos de su familia (ADR-2026-08-31-version2-vertientes-con-ilustracion), la
 * barra que presenta el Agente (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final)
 * y, en los rubros sin catálogo propio, el hero del Agente en lugar de las cards
 * (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).
 *
 * Copiado de `HomeMinimal` de `principal` (17d80457) sin cambios de comportamiento,
 * cuando las dos cabeceras pasaron a convivir (ADR-2026-10-07-la-home-elige-su-cabecera):
 * ésta para el resto, `CabeceraPorDestino` para el grupo C. `HomeMinimal` pone el saludo,
 * los accesos, la estela y todo lo de abajo; acá va sólo lo que está entre los banners y
 * la card de Academy.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Image as ImageIcon, Play, Video as VideoIcon, X } from 'lucide-react';
import type { ModoHome } from './EstiloMinimal';
// La carátula de un modo sale del catálogo y de ningún otro
// lado — ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo.
import { caratulaModo } from '../v2/modesCatalog';
// El helper de autoplay de las carátulas en video: fuerza `muted`/`playsinline` como
// ATRIBUTO (iOS no autoplaya si sólo está la propiedad) y pausa el video cuando no se
// ve. Es el mismo que usan la tira de la home, `/modos` y el sheet de Generar.
import { setupAutoplayVideo } from '../v2/components/HomeCards';
// La grilla de modos del popup, que la solapa "Herramientas" del hero del Agente
// también dibuja (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).
import { GrillaDeModos } from '../v2/components/GrillaDeModos';
import { HeroAgenteHome } from '../v2/components/HeroAgenteHome';
// La barra que le presenta el Agente —sin desplegarlo— a los rubros con catálogo propio
// (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final). Es la contracara del
// hero: allá el Agente ES la home, acá es una puerta arriba de la elección.
import { PresentacionDelAgente } from '../v2/components/PresentacionDelAgente';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

type Vertiente = 'imagen' | 'video';

/** Copy de las dos cards de vertiente y de su popup. */
const VERTIENTES: Record<
  Vertiente,
  { titulo: string; bajada: string; tituloPopup: string }
> = {
  imagen: {
    titulo: 'Crear imágenes',
    get bajada() {
      return `Fotos de producto, catálogo, ${palabraDeModeloActual().plural} y anuncios listos para publicar.`;
    },
    tituloPopup: '¿Cómo querés crear tu imagen?',
  },
  video: {
    titulo: 'Crear videos',
    bajada: 'Animá una foto o generá un clip con un influencer IA que lo presente.',
    tituloPopup: '¿Cómo querés crear tu video?',
  },
};

/**
 * Las piezas de la ilustración de "Crear videos", ELEGIDAS a mano.
 *
 * Antes las tres salían del catálogo (`caratulaModo` de los primeros modos de la
 * familia) y las dos de atrás eran la MISMA carátula repetida, la del segundo modo:
 * la de Animar imagen, que es una captura de la pantalla del modo y que a 26% de
 * ancho, rotada y al 45% de opacidad se leía como dos rectángulos grises. La
 * ilustración tiene que mostrar de qué es capaz la vertiente, y para eso las piezas
 * se eligen — no se derivan de en qué orden quedó el catálogo.
 *
 * Los archivos van en `public/images/home_vertientes/`. Cada pieza puede ser video
 * (loop, silenciado, con `poster` opcional para el primer cuadro) o sólo foto.
 * Un hueco vacío vuelve a llenarse con la carátula del catálogo que iba ahí, así
 * que la lista puede quedar corta o vacía y la card sigue andando.
 */
/** Las dos de atrás: izquierda y derecha, en ese orden. */
const PORTADAS_VIDEO_DE_ATRAS: { poster: string; video?: string }[] = [
  // Con su primer cuadro: sin poster la ficha quedaba vacía hasta que llegaba el clip
  // (ADR-2026-10-18-las-fichas-de-la-home-y-crear-usan-miniaturas).
  { poster: '/images/home_vertientes/crear_videos_atras_1.jpg', video: '/images/home_vertientes/crear_videos_atras_1.mp4' },
  { poster: '/images/home_vertientes/crear_videos_atras_2.jpg', video: '/images/home_vertientes/crear_videos_atras_2.mp4' },
];

/**
 * La del frente, la que lleva la pastilla de play. Vacía = la del catálogo (hoy, el
 * clip del primer modo de video). Para cambiarla, una entrada acá.
 */
const PORTADA_VIDEO_DEL_FRENTE: { poster: string; video?: string } | undefined =
  undefined;

/**
 * Una pieza del abanico: video en loop si lo tiene, foto si no.
 *
 * El `ref={setupAutoplayVideo}` es el mismo helper que la tira de la home: fuerza
 * `muted`/`playsinline` como ATRIBUTO (iOS no autoplaya con sólo la propiedad) y
 * pausa el video cuando la card no se ve, que con tres videos en una card importa.
 */
function PiezaDeVertiente({ pieza }: { pieza?: { poster: string; video?: string } }) {
  if (pieza?.video) {
    return (
      <video
        ref={setupAutoplayVideo}
        src={pieza.video}
        poster={pieza.poster || undefined}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        className="h-full w-full object-cover"
      />
    );
  }
  return <img src={pieza?.poster} alt="" className="h-full w-full object-cover" />;
}

/**
 * La ILUSTRACIÓN de una card de vertiente — ADR-2026-08-31-version2-vertientes-con-ilustracion.
 *
 * No es una foto de fondo a sangre sino una composición que dice qué ES la vertiente:
 *
 *  • **Imagen** — tres carátulas en abanico, ligeramente rotadas y superpuestas. Lo que
 *    la vertiente entrega son VARIAS fotos, y un abanico de tres lo dice de un vistazo;
 *    una sola foto de fondo se lee como "una foto", que es justo lo contrario.
 *  • **Video** — un cuadro vertical al frente REPRODUCIENDO (el mismo clip que la tira
 *    usa de carátula) con la pastilla de play encima, y dos marcos apilados detrás. El
 *    movimiento es lo que distingue a un video de una imagen, así que la ilustración se
 *    mueve.
 *
 * Las piezas son las carátulas REALES de los modos de esa familia, no un asset nuevo:
 * la ilustración cambia sola si cambia el catálogo, y no hay un archivo de diseño más
 * que mantener y que se desactualice cuando entra o sale un modo.
 */
function IlustracionVertiente({
  vertiente,
  caratulas,
}: {
  vertiente: Vertiente;
  caratulas: { poster: string; video?: string }[];
}) {
  // Marco común de las piezas: borde claro + sombra, para que se lean como fotos
  // apoyadas sobre la card y no como recortes pegados.
  const marco =
    'absolute overflow-hidden rounded-xl border border-white/70 bg-[#F2F3F5] shadow-[0_10px_30px_rgba(16,24,45,0.18)]';

  if (vertiente === 'video') {
    // Las piezas elegidas a mano mandan; los huecos que dejen se llenan con las
    // carátulas del catálogo, como antes.
    const frente = PORTADA_VIDEO_DEL_FRENTE ?? caratulas[0];
    const [izquierda, derecha] = [0, 1].map(
      (i) => PORTADAS_VIDEO_DE_ATRAS[i] ?? caratulas[i + 1] ?? caratulas[0],
    );
    return (
      <span aria-hidden="true" className="absolute inset-0">
        {/* Los dos marcos de atrás, apilados y girados: sugieren que hay más de un
            clip sin competir con el del frente. Son DOS clips distintos, no el mismo
            repetido: repetido, el abanico se lee como un error de render.

            La opacidad subió de 45/60 a 80/90: el velo de antes estaba para disimular
            que las dos eran la misma captura de pantalla gris. Con clips de verdad
            detrás, a 45% se lavaban hasta no distinguirse del lavanda de la card —
            que es justo lo que se venía a arreglar. El que queden atrás ya lo dicen
            el tamaño, la rotación y el solape; no hace falta apagarlos. */}
        <span
          className={`${marco} left-[6%] top-[22%] h-[56%] w-[26%] opacity-80`}
          style={{ transform: 'rotate(-8deg)' }}
        >
          <PiezaDeVertiente pieza={izquierda} />
        </span>
        <span
          className={`${marco} right-[8%] top-[16%] h-[60%] w-[26%] opacity-90`}
          style={{ transform: 'rotate(7deg)' }}
        >
          <PiezaDeVertiente pieza={derecha} />
        </span>
        {/* El cuadro del frente, vertical (9:16 es la forma en la que se publica) y en
            movimiento cuando la pieza trae clip. */}
        <span className={`${marco} left-1/2 top-1/2 h-[74%] w-[32%] -translate-x-1/2 -translate-y-1/2`}>
          <PiezaDeVertiente pieza={frente} />
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/90 text-[#16181D] shadow-md backdrop-blur-sm">
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            </span>
          </span>
        </span>
      </span>
    );
  }

  // Imagen: abanico de tres. Si la familia tuviera menos carátulas, se repiten — la
  // composición es la que cuenta, no cuál foto va en cada hueco.
  const [a, b, c] = [0, 1, 2].map((i) => caratulas[i] ?? caratulas[0]);
  return (
    <span aria-hidden="true" className="absolute inset-0">
      <span
        className={`${marco} left-[4%] top-[24%] h-[54%] w-[30%]`}
        style={{ transform: 'rotate(-9deg)' }}
      >
        <img src={a?.poster} alt="" className="h-full w-full object-cover" />
      </span>
      <span
        className={`${marco} right-[6%] top-[22%] h-[56%] w-[30%]`}
        style={{ transform: 'rotate(9deg)' }}
      >
        <img src={c?.poster} alt="" className="h-full w-full object-cover" />
      </span>
      {/* La del medio va al frente y un poco más grande: es el eje del abanico. */}
      <span className={`${marco} left-1/2 top-1/2 h-[66%] w-[33%] -translate-x-1/2 -translate-y-1/2`}>
        <img src={b?.poster} alt="" className="h-full w-full object-cover" />
      </span>
    </span>
  );
}

/**
 * Card de VERTIENTE — la que abre el popup. No navega a ningún lado: su trabajo es
 * separar la pregunta "¿imagen o video?" de la pregunta "¿con qué herramienta?".
 *
 * El fondo ya NO es la carátula de un modo a sangre
 * (ADR-2026-08-31-version2-vertientes-con-ilustracion): con una foto de producto de
 * fondo, las dos cards se leían como dos modos más y no como dos CAMINOS. Ahora cada
 * una es una superficie plana con la ilustración de su vertiente al costado.
 *
 * Las dos van CLARAS (ADR-2026-09-01-la-card-de-video-deja-el-negro, supersede el
 * "video en oscuro" de aquella entrada): el panel negro se llevaba la mirada de toda la
 * home apenas se abría, así que la de videos pesaba más que la de imágenes cuando las
 * dos son el mismo paso. Lo que las distingue ahora es el TINTE —azul frío para
 * imágenes, lavanda para videos— y el color de la flecha: azul de marca en imágenes,
 * tinta en videos. Diferencia de matiz, no de peso.
 */
function CardVertiente({
  vertiente,
  cantidad,
  caratulas,
  onElegir,
}: {
  vertiente: Vertiente;
  cantidad: number;
  caratulas: { poster: string; video?: string }[];
  onElegir: () => void;
}) {
  const copy = VERTIENTES[vertiente];
  const IconoVertiente = vertiente === 'video' ? VideoIcon : ImageIcon;
  const esVideo = vertiente === 'video';
  return (
    <button
      type="button"
      onClick={onElegir}
      // `aria-haspopup`: el botón no lleva a otra pantalla, abre un diálogo.
      aria-haspopup="dialog"
      // Los dos tintes tienen la misma luminosidad: puestos uno al lado del otro
      // ninguno se adelanta, que es justamente lo que hacía el panel negro.
      className={`group relative h-[230px] flex-1 overflow-hidden rounded-2xl border border-black/[0.06] text-left transition-shadow hover:shadow-[0_10px_36px_rgba(16,24,45,0.12)] ${
        esVideo ? 'bg-[#EFEBF6]' : 'bg-[#EDEFF5]'
      }`}
    >
      {/* La ilustración ocupa la mitad derecha. El wrapper la recorta y le da el
          hover: `scale` sobre el contenedor mueve el abanico entero, que es lo que se
          quiere; sobre cada pieza, cada una se movería por su cuenta. */}
      <span className="absolute inset-y-0 right-0 w-[48%] transition-transform duration-700 group-hover:scale-[1.04]">
        <IlustracionVertiente vertiente={vertiente} caratulas={caratulas} />
      </span>

      <span className="relative z-10 flex h-full w-[56%] flex-col justify-end gap-1.5 p-6">
        <span
          className="flex items-center gap-2 text-[12.5px] font-semibold uppercase tracking-[0.08em] text-black/45"
        >
          <IconoVertiente className="h-4 w-4" />
          {cantidad === 1 ? '1 herramienta' : `${cantidad} herramientas`}
        </span>
        <span
          className="block text-[24px] font-bold leading-tight tracking-[-0.02em] text-brand-black/90"
        >
          {copy.titulo}
        </span>
        <span
          className="block text-[13.5px] leading-snug text-black/50"
        >
          {copy.bajada}
        </span>
        <span
          // La flecha es lo que queda distinguiendo a las dos cards de un vistazo:
          // azul de marca en imágenes, tinta en videos.
          className={`mt-2 grid h-10 w-10 place-items-center rounded-full text-white transition-transform duration-300 group-hover:translate-x-1 ${
            esVideo ? 'bg-[#16181D]' : 'bg-brand-primary'
          }`}
        >
          <ArrowRight className="h-[18px] w-[18px]" />
        </span>
      </span>
    </button>
  );
}

/**
 * El popup con los modos de una vertiente.
 *
 * Va por `createPortal` a `document.body` y no en su lugar del árbol: el shell envuelve
 * el contenido en contenedores con `transform` para el fade entre secciones, y un
 * `position: fixed` dentro de un ancestro transformado se posiciona contra ESE ancestro
 * en vez de contra el viewport — el overlay habría quedado corrido. Es el mismo motivo
 * por el que el menú de cuenta del shell también se portalea.
 */
function PopupPorVertiente({
  vertiente,
  modos,
  rubroMarca,
  onAbrir,
  onCerrar,
}: {
  vertiente: Vertiente;
  modos: ModoHome[];
  rubroMarca: string | null;
  onAbrir: (m: ModoHome) => void;
  onCerrar: () => void;
}) {
  // Escape cierra. Se escucha en `document` y no en el diálogo para que funcione sin
  // depender de dónde quedó el foco al abrir.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={VERTIENTES[vertiente].tituloPopup}
    >
      {/* El fondo cierra al click. Es un `button` y no un `div` con `onClick` para que
          ese cierre también esté al alcance del teclado. */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]"
      />
      {/* `max-h-[85vh]` + scroll propio: con 7 modos la grilla mide tres filas y en
          una pantalla de 800px de alto el diálogo se salía por abajo. */}
      <div className="animate-fade-in relative z-10 max-h-[85vh] w-full max-w-[860px] overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_24px_80px_rgba(16,24,45,0.28)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">
              {VERTIENTES[vertiente].tituloPopup}
            </h2>
            <p className="mt-1 text-[13.5px] text-black/50">
              Elegí la herramienta con la que querés empezar.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-black/50 transition-colors hover:bg-black/[0.05] hover:text-black/80"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <GrillaDeModos
          modos={modos}
          rubroMarca={rubroMarca}
          onAbrir={onAbrir}
          className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3"
        />
      </div>
    </div>,
    document.body,
  );
}

export function CabeceraPorVertiente({
  modos,
  rubroMarca,
  abrirModo,
  abreConElAgente,
  agenteBloqueado,
  conPresentacionDelAgente,
  saludo,
  onIrAPlanes,
}: {
  modos: ModoHome[];
  rubroMarca: string | null;
  /** Abrir un modo (candado → ejemplos o planes; si no, el modo con el fade del shell). */
  abrirModo: (m: ModoHome) => void;
  /** El hero ES el Agente (rubros sin catálogo propio) en vez de las dos cards. */
  abreConElAgente: boolean;
  /** El plan no incluye el Agente: la caja del hero manda a los planes. */
  agenteBloqueado: boolean;
  /** La barra que presenta el Agente arriba de las cards (sólo si no abre con él). */
  conPresentacionDelAgente: boolean;
  /** El saludo grande, para el centro del hero del Agente (lo arma `HomeMinimal`). */
  saludo: ReactNode;
  /** El mismo catálogo de planes que abre cualquier card bloqueada de la home. */
  onIrAPlanes: () => void;
}) {
  /** Qué popup está abierto. `null` = ninguno, que es como abre la pantalla. */
  const [popup, setPopup] = useState<Vertiente | null>(null);

  const porVertiente = useMemo(() => {
    // Sin `familia` (no debería pasar: la pone `InterfazV2`) el modo cae en imagen, el
    // mismo defecto que usa `useModosGenerar`.
    const imagen = modos.filter((m) => m.familia !== 'video');
    const video = modos.filter((m) => m.familia === 'video');
    return { imagen, video };
  }, [modos]);

  // El popup se cierra ANTES de navegar: el fade de salida del shell apaga el
  // contenido de la home, y el overlay —que cuelga de `body`— no se enteraría.
  const abrir = (m: ModoHome) => {
    setPopup(null);
    abrirModo(m);
  };

  return (
    <>
      {/* ── El Agente, presentado ─────────────────────────────────────────────
          Lo primero después del saludo y los banners, ARRIBA de "Elegí qué querés
          crear" (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final). Acá
          arriba vivía "Nuevo en Atlas", la tira de los dos modos recién salidos: se
          fue, y la barra del Agente ocupa su lugar.

          Va sólo en la rama de la home de siempre, y no por dónde quedó escrita: en la
          otra, el hero ES el Agente. `conPresentacionDelAgente` la apaga justamente
          para esos rubros. El copy lo elige el rubro adentro del componente. */}
      {!abreConElAgente && conPresentacionDelAgente && (
        <PresentacionDelAgente
          rubro={rubroMarca}
          bloqueado={agenteBloqueado}
          onIrAPlanes={onIrAPlanes}
        />
      )}

      {abreConElAgente ? (
        /* ── El Agente, primero ──────────────────────────────────────────────
           La caja escribible arriba de todo, y los modos en la otra mitad del switch.
           Las cards de vertiente y su popup no se dibujan en esta rama: el hero ya
           lleva a las dos familias, y con las dos cosas a la vista la caja dejaría de
           ser la primera instancia. */
        <HeroAgenteHome
          saludo={saludo}
          modos={modos}
          rubroMarca={rubroMarca}
          agenteBloqueado={agenteBloqueado}
          onAbrirModo={abrir}
          onIrAPlanes={onIrAPlanes}
        />
      ) : (
        <>
          {/* ── Vertiente ─────────────────────────────────────────────────────
              Las dos cards NO navegan: abren el popup con los modos de su familia. */}
          <section className="mt-8">
            <h2 className="text-[15px] font-semibold text-black/45">
              Elegí qué querés crear
            </h2>
            <div className="mt-3 flex gap-2.5">
              {(['imagen', 'video'] as const).map((v) =>
                porVertiente[v].length === 0 ? null : (
                  <CardVertiente
                    key={v}
                    vertiente={v}
                    cantidad={porVertiente[v].length}
                    // Las carátulas de los primeros modos de la familia, en el orden del
                    // catálogo: con ellas se arma la ilustración. Tres alcanzan para el
                    // abanico; si la familia tiene menos, `IlustracionVertiente` repite.
                    caratulas={porVertiente[v]
                      .slice(0, 3)
                      .map((m) => caratulaModo(m.path, rubroMarca, m.imagen))}
                    onElegir={() => setPopup(v)}
                  />
                ),
              )}
            </div>
          </section>

          {popup && (
            <PopupPorVertiente
              vertiente={popup}
              modos={porVertiente[popup]}
              rubroMarca={rubroMarca}
              onAbrir={abrir}
              onCerrar={() => setPopup(null)}
            />
          )}
        </>
      )}
    </>
  );
}
